import type { Circuit } from '../engine/circuit';
import type { Element } from '../engine/element';
import { instantiateElements, serializeElements, type SerializedElement } from '../engine/serial';
import type { SubcktDef } from '../engine/subckt';
import type { ViewState } from './render';

export type { SerializedElement };

export const FORMAT = 'circuitlab';
export const VERSION = 2;

export interface CircuitFile {
  format: typeof FORMAT;
  version: number;
  name: string;
  savedAt: string;
  view?: ViewState;
  elements: SerializedElement[];
  /** 이 회로가 쓰는 모듈 정의 (v2~) */
  subcircuits?: SubcktDef[];
}

export function serialize(circuit: Circuit, name: string, view?: ViewState): CircuitFile {
  const used = circuit.usedDefIds();
  return {
    format: FORMAT,
    version: VERSION,
    name,
    savedAt: new Date().toISOString(),
    view,
    elements: serializeElements(circuit.elements),
    subcircuits: [...circuit.defs.values()].filter((d) => used.has(d.id)),
  };
}

export function isCircuitFile(data: unknown): data is CircuitFile {
  if (!data || typeof data !== 'object') return false;
  const f = data as Partial<CircuitFile>;
  return f.format === FORMAT && Array.isArray(f.elements);
}

export function deserialize(file: CircuitFile): Element[] {
  return instantiateElements(file.elements, true);
}

/** 파일 내용을 회로에 채운다 (모듈 정의 포함) */
export function loadIntoCircuit(circuit: Circuit, file: CircuitFile): void {
  circuit.defs = new Map((file.subcircuits ?? []).map((d) => [d.id, d]));
  circuit.elements = deserialize(file);
  circuit.markDirty();
  circuit.reset();
}

/* ───────── 파일로 저장 / 열기 ───────── */

export function downloadCircuit(file: CircuitFile): void {
  const blob = new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${sanitize(file.name)}.circuit.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function pickCircuitFile(): Promise<CircuitFile> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.circuit.json,application/json';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return reject(new Error('파일을 고르지 않았어요'));
      try {
        const data = JSON.parse(await f.text());
        if (!isCircuitFile(data)) return reject(new Error('CircuitLab 회로 파일이 아니에요'));
        resolve(data);
      } catch (e) {
        reject(e instanceof Error ? e : new Error('파일을 읽을 수 없어요'));
      }
    };
    input.click();
  });
}

function sanitize(name: string): string {
  return (name || 'circuit').replace(/[\\/:*?"<>|]/g, '_').trim() || 'circuit';
}

/* ───────── 브라우저 안에 저장 (localStorage) ───────── */

const PREFIX = 'circuitlab:save:';
export const AUTOSAVE_KEY = 'circuitlab:autosave';

export interface SaveEntry {
  name: string;
  savedAt: string;
}

export function listSaves(): SaveEntry[] {
  const out: SaveEntry[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k?.startsWith(PREFIX)) continue;
    try {
      const f = JSON.parse(localStorage.getItem(k) ?? '') as CircuitFile;
      out.push({ name: f.name, savedAt: f.savedAt });
    } catch {
      /* 무시 */
    }
  }
  return out.sort((a, b) => b.savedAt.localeCompare(a.savedAt));
}

export function saveLocal(file: CircuitFile): void {
  localStorage.setItem(PREFIX + file.name, JSON.stringify(file));
}

export function loadLocal(name: string): CircuitFile | null {
  const raw = localStorage.getItem(PREFIX + name);
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    return isCircuitFile(data) ? data : null;
  } catch {
    return null;
  }
}

export function deleteLocal(name: string): void {
  localStorage.removeItem(PREFIX + name);
}

export function saveAutosave(file: CircuitFile): void {
  try {
    localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(file));
  } catch {
    /* 용량 초과 등은 무시 */
  }
}

export function loadAutosave(): CircuitFile | null {
  const raw = localStorage.getItem(AUTOSAVE_KEY);
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    return isCircuitFile(data) ? data : null;
  } catch {
    return null;
  }
}

/* ───────── 내 모듈 라이브러리 (브라우저에 저장, 모든 회로에서 공유) ───────── */

const LIB_KEY = 'circuitlab:modules';

export function loadLibrary(): SubcktDef[] {
  try {
    const raw = localStorage.getItem(LIB_KEY);
    const list = raw ? (JSON.parse(raw) as SubcktDef[]) : [];
    return Array.isArray(list) ? list.filter((d) => d && d.id && Array.isArray(d.elements)) : [];
  } catch {
    return [];
  }
}

export function saveLibrary(list: SubcktDef[]): void {
  try {
    localStorage.setItem(LIB_KEY, JSON.stringify(list));
  } catch {
    /* 무시 */
  }
}

export function upsertLibrary(def: SubcktDef): void {
  const list = loadLibrary().filter((d) => d.id !== def.id);
  list.push(def);
  saveLibrary(list);
}

export function removeFromLibrary(id: string): void {
  saveLibrary(loadLibrary().filter((d) => d.id !== id));
}

/** 모듈 하나를 파일로 내보내기 */
export function downloadModule(def: SubcktDef): void {
  const blob = new Blob([JSON.stringify({ format: 'circuitlab-module', version: 1, module: def }, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${sanitize(def.name)}.module.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
